import type { UserLicense, TenantHealth, ClientLicenseAccessDto } from './License';

export enum DimensionDisplayType {
    Unframed = 1,
    Framed = 2,
    Both = 3
}
export enum DimensionUnitType {
    Metric = 1,
    Imperial = 2
}

export type TenantRef = {
    tenantId: number;
    name: string;
    isDefault: boolean;
};

export interface ClientAccess {
    clientId: number;
    clientName: string;
    roleCode: string;
    roleName: string;
    tenantId?: number;
    isTenantAdmin: boolean;
    hasClientWideRole: boolean;
    defaultPortfolioId: number;
    tenantRoleCode?: string;
    tenantRoleName?: string;
    licenseAccess?: ClientLicenseAccessDto | null;

    tenantName?: string | null;
    defaultImageUrl?: string | null;
    artworkCount: number;
    isDefaultName: boolean;
}
export interface UserRecord {
    userId: number;
    email: string;
    displayName: string;
    canValueArtworks: boolean;
    isExternalContactOnly: boolean;
    hasLogin: boolean;
    defaultClientId?: number;
    hasAcceptedTerms?: boolean;
    termVersionAccepted?: string;
    dateTermsAccepted?: Date;
    clients?: ClientAccess[];
    system?: SystemAccessDto;

    defaultTenantId?: number | null;
    tenants?: TenantRef[];
    license?: UserLicense;
    tenantHealth?: TenantHealth;

    compactView: boolean;
    showValues: boolean;
    dimensionUnitPreference: DimensionUnitType;
    dimensionDisplayPreference: DimensionDisplayType;
}

export interface UserSettingsPreferencesDto {
    compactView: boolean;
    showValues: boolean;
    dimensionUnitPreference: DimensionUnitType;
    dimensionDisplayPreference: DimensionDisplayType;
}

export interface UserRole {
    id: number;
    role: string;
    description?: string | null;
}
export interface ClientUser {
    userId: number;
    email: string;
    displayName: string;
    roleId: number;
}

export type ClientRoleDto = {
    clientId: number;
    clientName: string;
    role: string;
};

export type SystemAccessDto = {
    roles: string[];
    permissions: string[];
};

export type MeDto = {
    userId: number;
    email: string;
    displayName?: string | null;
    defaultClientId: number;

    hasAcceptedTerms: boolean;
    termVersionAccepted?: string | null;
    dateTermsAccepted?: string | null;

    clientRoles: ClientRoleDto[];
    system: SystemAccessDto;

    defaultTenantId?: number | null;
    tenants?: TenantRef[];
};

export interface PortfolioUserAccess {
    userId: number;
    email: string;
    displayName?: string;
    roleId: number;
    role?: string;
    isActive: boolean;
}

export type PortfolioAccessRow =
    | {
        kind: "user";
        userId: number;
        email: string;
        displayName?: string;
        roleId: number;
        role: string;
        isActive: boolean;
    }
    | {
        kind: "invite";
        inviteId: string; // Guid
        email: string;
        roleId: number;
        role: string;
        createdAt: string;
        expiresAt: string;
        isExpired: boolean;
    };

export type PortfolioAccessScope = "Client" | "Portfolio";
export type PortfolioAccessKind = "User" | "Invite";

export interface PortfolioAccessRowDto {
    kind: PortfolioAccessKind;
    scope: PortfolioAccessScope;

    email: string;
    displayName?: string | null;

    roleId: number;
    role: string;

    // user row
    userId?: number | null;
    isActive?: boolean | null;
    clientUserRoleId?: number | null;

    // invite row
    inviteId?: string | null;     // Guid string
    expiresAt?: string | null;    // ISO
    isExpired?: boolean | null;
}

export type AccessRow =
    | { type: "user"; userId: number; email: string; displayName?: string; roleId: number; role: string; isActive: boolean; portfolioId?: number | null; portfolioName?: string | null; scopeLabel: string; }
    | { type: "invite"; invitationId: string; email: string; roleId: number; role: string; expiresAt: string; isExpired: boolean; portfolioId?: number | null; portfolioName?: string | null; scopeLabel: string; };

export interface AccessListDto {
    users: {
        userId: number;
        email: string;
        displayName?: string | null;
        roleId: number;
        role: string;
        isActive: boolean;
        scope: string;
        portfolioId?: number | null;
        portfolioName?: string | null;
    }[];
    invites: {
        invitationId: string;
        email: string;
        roleId: number;
        role: string;
        expiresAt: string;
        isExpired: boolean;
        portfolioId?: number | null;
        scope: string;
        portfolioName?: string | null;
    }[];
    roles: { id: number; role: string; description?: string | null }[];
}

export type AccessScope =
    | { kind: "client"; clientId: number }
    | { kind: "portfolio"; clientId: number; portfolioId: number };

export type UserAccessDto = {
    kind: "User" | "Invite";
    scope: "Client" | "Portfolio";

    email: string;
    displayName?: string | null;

    roleId: number;
    role: string;

    isActive: boolean;
    isExpired: boolean;
    expiresAt?: string | null;

    clientUserRoleId?: number | null;
    userId?: number | null;
    inviteId?: string | null;

    portfolioId?: number | null;
    portfolioName?: string | null;
};